import {useQuery} from '@tanstack/react-query'
import {Tag} from 'lucide-react'
import {useParams} from 'react-router-dom'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {usePageTitle} from '@/hooks/use-page-title'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {PageLayout} from '@/components/shared/PageLayout'
import {DetailsCard} from '@/components/shared/DetailsCard'
import {CardSection} from '@/components/shared/CardSection'
import {EntityLink} from '@/components/shared/EntityLink'
import {CodeText} from '@/components/shared/CodeText'
import {Icon} from '@/components/shared/Icon'
import {AccountStatusBadge} from '@/types/account'
import {CONCEPT_ICON} from '@/lib/concepts'
import {routes} from '@/lib/routes'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {formatTimestamp} from '@/lib/date-utils'
import {InlineAttributeEditor} from '@/components/attributes/InlineAttributeEditor'
import {AttributeEntityType} from '@/types/attribute-entity-type'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'

export function AccountDetail() {
    const services = useServices()
    const i18n = useI18n()
    const {hasPermission} = useAuth()
    usePageTitle(i18n.accounts.detailPageTitle)
    const {id} = useParams<{ id: string }>()
    const {accountStatuses, systemTypes, environmentTypes} = useAppConfig()
    const canEditAttributes = hasPermission(permissions.attributeDefinitions.accountUpdate)

    const {data: account, isLoading, error} = useQuery({
        queryKey: ['accounts', id],
        queryFn: () => services.accounts.detail(id!),
        enabled: !!id,
    })

    const statusBadge = account ? AccountStatusBadge.of(account.status) : undefined
    const StatusIcon = statusBadge?.icon

    return (
        <PageLayout
            data-testid="account-detail-page"
            variant="detail"
            title={account?.identity.displayName ?? ''}
            icon={CONCEPT_ICON.account}
            backTo={routes.identityManagement.accounts.list()}
            loading={isLoading}
            error={error || (!isLoading && !account ? new Error(i18n.accounts.notFound) : undefined)}
            actions={account ? (
                <Badge variant={statusBadge?.variant ?? BadgeVariant.OUTLINE} className="gap-1" data-testid="account-status-badge">
                    {StatusIcon && <Icon icon={StatusIcon} size="xs"/>}
                    {labelOf(accountStatuses, account.status)}
                </Badge>
            ) : undefined}
        >
            {account && (<>
                <DetailsCard
                    data-testid="card-account-details"
                    title={i18n.accounts.accountDetails}
                    fields={[
                        {
                            label: i18n.accounts.identity,
                            value: <EntityLink value={account.identity} testId="link-account-identity"/>,
                        },
                        {
                            label: i18n.accounts.targetSystem,
                            value: <EntityLink value={account.targetSystem} testId="link-account-system"/>,
                        },
                        {
                            label: i18n.accounts.systemType,
                            value: labelOf(systemTypes, account.targetSystemType),
                        },
                        {
                            label: i18n.accounts.environment,
                            value: labelOf(environmentTypes, account.targetSystem.subType ?? ''),
                        },
                        {
                            label: i18n.accounts.externalIdentifier,
                            value: <CodeText data-testid="account-external-identifier">{account.externalIdentifier}</CodeText>,
                        },
                        {
                            label: i18n.common.status,
                            value: labelOf(accountStatuses, account.status),
                        },
                        {
                            label: i18n.accounts.disabledAt,
                            value: account.disabledAt ? formatTimestamp(account.disabledAt) : '',
                        },
                        {label: i18n.common.createdAt, value: formatTimestamp(account.createdAt)},
                        {label: i18n.common.lastUpdated, value: formatTimestamp(account.updatedAt)},
                    ]}
                />

                <CardSection data-testid="section-account-attributes" icon={Tag} title={i18n.common.attributes}>
                    <InlineAttributeEditor
                        entityType={AttributeEntityType.ACCOUNT}
                        entityId={id!}
                        entityTypeCode={account.targetSystemType}
                        canEdit={canEditAttributes}
                    />
                </CardSection>
            </>)}
        </PageLayout>
    )
}
